import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  signal,
  OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize, map, tap } from 'rxjs';
import { SharedModule } from '~backoffice/app/~shared/modules/shared.module';
import { ResourceFacade } from '~backoffice/app/~store/facades/resource.facade';
import { DialogService } from '~core/services/dialog.service';
import { GlobalFacade } from '~backoffice/app/~store/facades/global.facade';
import { SnackbarService } from '~backoffice/app/~shared/services/snackbar.service';
import {
  LiveStream,
  LiveStreamCreationParams,
  LiveStreamEntry,
  LiveStreamEntryListParams
} from '~core/models/live-stream.models';
import { LiveStreamService } from '~core/services/live-stream.service';
import {
  MediaSourceStatus,
  MediaSourceType
} from '~core/models/media-source.models';
import {
  enumToArray,
  removeNilProperties
} from '~backoffice/app/~shared/utils/utils';

@Component({
  selector: 'edit-live-stream',
  imports: [SharedModule],
  standalone: true,
  templateUrl: './edit-live-stream.component.html',
  styleUrl: './edit-live-stream.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditLiveStreamComponent implements OnInit {
  private _dialogService = inject(DialogService);
  private _globalFacade = inject(GlobalFacade);
  private _cdRef = inject(ChangeDetectorRef);
  private _liveStreamService = inject(LiveStreamService);
  private _resourceFacade = inject(ResourceFacade);
  private _destroyRef = inject(DestroyRef);
  private _snackbarService = inject(SnackbarService);

  liveStream = input<LiveStream | null | undefined>();

  isLoading = signal<boolean>(false);
  isSubmitting = signal(false);

  form = new FormGroup({
    title: new FormControl<string>('', [
      Validators.required,
      Validators.minLength(2)
    ]),
    description: new FormControl<string>(''),
    type: new FormControl<string>('', [Validators.required]),
    source: new FormControl<string>('', [Validators.required]),
    markedAsDefault: new FormControl<boolean>(false)
  });

  kalturaMediaEntry: LiveStreamEntry | undefined;

  mediaSourceTypeArray = enumToArray(MediaSourceType);
  mediaSourceType = MediaSourceType;

  liveStreamsEntry$ = this._liveStreamService
    .getLiveStreamEntry({ itemPerPage: 1000 } as LiveStreamEntryListParams)
    .pipe(map(value => value.items));

  violations$ = this._globalFacade.formViolations$.pipe(
    tap({
      next: violations => {
        setTimeout(() => {
          violations?.forEach(violation =>
            this.form.get(violation.propertyPath)?.setErrors({
              httpError: violation.title
            })
          );
          this._cdRef.markForCheck();
        }, 500);
      }
    })
  );

  ngOnInit(): void {
    const liveStream = this.liveStream();
    if (liveStream) {
      this.patchFormValues(liveStream);
    }
  }

  onChangeKalturaMediaEntry(value: LiveStreamEntry): void {
    this.kalturaMediaEntry = value;
  }

  patchFormValues(liveStream: LiveStream | null | undefined): void {
    if (liveStream) {
      this.form.patchValue({
        title: liveStream.title,
        markedAsDefault: liveStream.markedAsDefault,
        description: liveStream.description,
        type: liveStream.mediaSource.type,
        source: liveStream.mediaSource.id
      });
      this._cdRef.detectChanges();
    }
  }

  onSubmit(): void {
    this.isSubmitting.set(true);
    const liveStreamId = this.liveStream()?.id;
    if (liveStreamId) {
      this.updateLiveStream();
    } else {
      this.createLiveStream();
    }
  }

  createLiveStream(): void {
    const formValue = {
      ...this.form.value,
      mediaSource: {
        id: this.form.value.source,
        type: this.form.value.type,
        status: this.kalturaMediaEntry
          ? this.kalturaMediaEntry.status
          : MediaSourceStatus.ready
      }
    };

    delete formValue.type;
    delete formValue.source;
    const dataWithoutEmpty = removeNilProperties(
      formValue
    ) as LiveStreamCreationParams;
    this.isLoading.set(true);
    this._liveStreamService
      .create(dataWithoutEmpty)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._dialogService.closeAll();
            this._snackbarService.open({
              text: 'Live Stream créé avec succès',
              type: 'success'
            });
            this._resourceFacade.getData({ refresh: true });
          },
          error: () => {
            this._snackbarService.open({
              text: 'Une erreur est survenue, Veuillez réessayer',
              type: 'error'
            });
          }
        }),
        finalize(() => {
          this.isLoading.set(false);
          this.isSubmitting.set(false);
          this._cdRef.detectChanges();
        })
      )
      .subscribe();
  }

  updateLiveStream(): void {
    const formValue = {
      ...this.form.value,
      mediaSource: {
        id: this.form.value.source,
        type: this.form.value.type
      }
    };

    delete formValue.type;
    delete formValue.source;
    const dataWithoutEmpty = removeNilProperties(
      formValue
    ) as LiveStreamCreationParams;

    const liveStream = this.liveStream();
    if (!liveStream?.id) return;

    this.isLoading.set(true);

    this._liveStreamService
      .update(liveStream.id, dataWithoutEmpty)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._dialogService.closeAll();
            this._snackbarService.open({
              text: 'Live Stream modifié avec succès',
              type: 'success'
            });
            this._resourceFacade.getData({ refresh: true });
          }
        }),
        finalize(() => {
          this.isLoading.set(false);
          this.isSubmitting.set(false);
          this._cdRef.detectChanges();
        })
      )
      .subscribe();
  }

  get modalTitle(): string {
    return this.liveStream()
      ? `Modification du liveStream`
      : `Créer un nouveau liveStream`;
  }

  get modalDescription(): string {
    return this.liveStream()
      ? `Veuillez éditer les champs ci-dessous pour mettre à jour le live stream`
      : `Veuillez remplir les champs ci-dessous pour la création d'un nouveau live stream`;
  }
}
